import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Box, LinearProgress, Radio, RadioGroup, FormControlLabel, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import CommonModalLayout from '../../../form-components/CommonModalLayout';
import CommonModalButton from '../../../form-components/CommonModalButton';
import { errorMessages } from '../../../utils/methods';
import { bulkCreateCenterManagers } from '../../../api/member-management';
import { parseCorpExcelRows } from './parseCorpExcelRows';
import { geocodeCorpExcelRows } from './geocodeCorpExcelRows';
import CorpExcelUploadPreviewTable from './CorpExcelUploadPreviewTable';
import { STATUS_OPTIONS, PLAN_TYPE_OPTIONS, DEFAULT_ROW_STATUS, DEFAULT_ROW_PLAN_TYPE } from './corpExcelUploadOptions';

const STEP = {
  SELECT: 'SELECT',
  GEOCODING: 'GEOCODING',
  PREVIEW: 'PREVIEW',
  SUBMITTING: 'SUBMITTING',
};

const FieldRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
});

const FieldLabel = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#333',
  minWidth: '110px',
});

const FileButton = styled('label')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '38px',
  padding: '0 16px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#0F7F85',
  background: '#F8FBFB',
  border: '1px solid #0F7F85',
  cursor: 'pointer',
});

const FileName = styled(Typography)({
  fontSize: '13px',
  color: '#333',
});

const NoticeBox = styled(Box)({
  padding: '10px 12px',
  background: '#fff5f5',
  color: '#dc2626',
  borderRadius: '8px',
  fontSize: '12px',
  marginBottom: '16px',
});

const InfoBox = styled(Box)({
  padding: '10px 12px',
  background: '#F0F7F7',
  color: '#3D3D3D',
  borderRadius: '8px',
  fontSize: '12px',
  lineHeight: '18px',
  marginBottom: '16px',
});

const ProgressLabel = styled(Typography)({
  fontSize: '12px',
  color: '#6b7280',
  marginTop: '8px',
});

const TooltipRow = styled('div')({
  fontSize: '12px',
  lineHeight: '18px',
});

const BrandRadio = styled(Radio)({
  color: '#aaa',
  '&.Mui-checked': {
    color: '#0F7F85',
  },
});

const BrandLinearProgress = styled(LinearProgress)({
  backgroundColor: '#e6f0ef',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#0F7F85',
  },
});

const buildPayloadRow = row => ({
  managerInfo: {
    username: row.username,
    name: row.representativeName,
    password: row.password,
    phoneNumber: row.phoneNumber || null,
    email: row.email || null,
  },
  centerInfo: {
    businessName: row.branchName,
    businessNumber: row.businessNumber,
    businessType: row.businessType || null,
    bankName: row.bankName || null,
    accountNumber: row.accountNumber || null,
    accountHolder: row.accountHolder || null,
    postalCode: row.postalCode,
    city: row.city,
    latitude: row.latitude,
    longitude: row.longitude,
    address: row.address,
    addressDetail: row.addressDetail || null,
    representativeNumber: row.representativeNumber || null,
    email: row.email || null,
    planType: row.planType,
  },
  status: row.status,
});

const CorpExcelUploadModal = ({ open, onClose, onUploaded }) => {
  const token = useSelector(state => state.user.user.token);
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(STEP.SELECT);
  const [fileName, setFileName] = useState('');
  const [defaultStatus, setDefaultStatus] = useState(DEFAULT_ROW_STATUS);
  const [defaultPlanType, setDefaultPlanType] = useState(DEFAULT_ROW_PLAN_TYPE);
  const [notice, setNotice] = useState('');
  const [excluded, setExcluded] = useState([]);
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const resetState = () => {
    setStep(STEP.SELECT);
    setFileName('');
    setNotice('');
    setExcluded([]);
    setRows([]);
    setProgress({ done: 0, total: 0 });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setFileName(file.name);
    setNotice('');

    let sheetRows;
    try {
      const workbook = XLSX.read(await file.arrayBuffer());
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      sheetRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
    } catch {
      setNotice('엑셀 파일을 읽지 못했습니다. 파일 형식을 확인해 주세요.');
      return;
    }

    const { missingColumns, excluded: excludedRows, rows: parsedRows } = parseCorpExcelRows(sheetRows);
    if (missingColumns) {
      setNotice("엑셀 컬럼을 확인할 수 없습니다. '가맹점명단(입력)' 시트의 헤더(지점명~비밀번호, 14열)가 그대로인지 확인해 주세요.");
      return;
    }
    if (!parsedRows.length) {
      setNotice('등록할 행이 없습니다 (전부 자동 제외되었거나 빈 시트입니다).');
      setExcluded(excludedRows);
      return;
    }

    setExcluded(excludedRows);
    setStep(STEP.GEOCODING);
    setProgress({ done: 0, total: parsedRows.length });

    const geocoded = await geocodeCorpExcelRows(parsedRows, (done, total) => setProgress({ done, total }));
    const withGeocodeErrors = geocoded.map(row => {
      const geocodeFailed = !(row.postalCode && row.city);
      return {
        ...row,
        status: defaultStatus,
        planType: defaultPlanType,
        errors: geocodeFailed ? [...row.errors, '주소 지오코딩 실패(우편번호/시군구 확인 필요)'] : row.errors,
      };
    });

    setRows(withGeocodeErrors);
    setStep(STEP.PREVIEW);
  };

  // 미리보기에서 행 하나의 계정상태/요금제를 개별로 바꿀 때. 파일 선택 화면의 기본값은
  // "전체 적용"(파싱 시점에 모든 행에 한 번에 채워짐) 역할이고, 이후엔 행별로 덮어쓸 수 있다.
  const handleRowFieldChange = (rowNumber, field, value) => {
    setRows(prev => prev.map(row => (row.rowNumber === rowNumber ? { ...row, [field]: value } : row)));
  };

  const errorRows = rows.filter(row => row.errors.length > 0);
  const hasErrorRows = errorRows.length > 0;
  const missingFieldsTooltip = hasErrorRows ? (
    <Box>
      {errorRows.map(row => (
        <TooltipRow key={row.rowNumber}>
          {row.rowNumber}행 ({row.branchName || '지점명 없음'}): {row.errors.join(', ')}
        </TooltipRow>
      ))}
    </Box>
  ) : '';

  const handleSubmit = async () => {
    if (!rows.length || hasErrorRows) return;
    setStep(STEP.SUBMITTING);
    try {
      const message = await bulkCreateCenterManagers(token, {
        rows: rows.map(buildPayloadRow),
        defaultStatus,
      });
      toast.success(message?.message || '일괄 등록되었습니다');
      resetState();
      onUploaded?.();
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
      setStep(STEP.PREVIEW);
    }
  };

  return (
    <CommonModalLayout
      open={open}
      onClose={handleClose}
      title="기업회원 엑셀 업로드"
      width={960}
      footer={
        <>
          <CommonModalButton variant="cancel" onClick={handleClose}>
            취소
          </CommonModalButton>
          {step === STEP.PREVIEW && (
            <>
              <CommonModalButton variant="cancel" onClick={resetState}>
                다시 선택
              </CommonModalButton>
              <Tooltip title={missingFieldsTooltip} disableHoverListener={!hasErrorRows} placement="top">
                <span>
                  <CommonModalButton variant="submit" onClick={handleSubmit} disabled={hasErrorRows || !rows.length}>
                    {rows.length - errorRows.length}건 일괄 등록
                  </CommonModalButton>
                </span>
              </Tooltip>
            </>
          )}
          {step === STEP.SUBMITTING && (
            <CommonModalButton variant="submit" disabled>
              등록 중...
            </CommonModalButton>
          )}
        </>
      }>
      <InfoBox>
        '바디스위치_기업회원양식.xlsx' 형식의 엑셀만 지원합니다. 필수값(지점명·대표자명·사업자번호·업태·주소·아이디·비밀번호)이 없는 행이 하나라도 있으면 업로드할 수 없습니다.
      </InfoBox>
      {notice && <NoticeBox>{notice}</NoticeBox>}

      {(step === STEP.SELECT || step === STEP.GEOCODING) && (
        <>
          <FieldRow>
            <FieldLabel>계정 상태 기본값</FieldLabel>
            <RadioGroup row value={defaultStatus} onChange={e => setDefaultStatus(e.target.value)}>
              {STATUS_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<BrandRadio size="small" disabled={step === STEP.GEOCODING} />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FieldRow>

          <FieldRow>
            <FieldLabel>요금제 기본값</FieldLabel>
            <RadioGroup row value={defaultPlanType} onChange={e => setDefaultPlanType(e.target.value)}>
              {PLAN_TYPE_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<BrandRadio size="small" disabled={step === STEP.GEOCODING} />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FieldRow>

          <FieldRow>
            <FieldLabel>엑셀 파일</FieldLabel>
            <FileButton>
              파일 선택
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={step === STEP.GEOCODING}
              />
            </FileButton>
            {fileName && <FileName>{fileName}</FileName>}
          </FieldRow>

          {step === STEP.GEOCODING && (
            <Box>
              <BrandLinearProgress
                variant="determinate"
                value={progress.total ? (progress.done / progress.total) * 100 : 0}
              />
              <ProgressLabel>
                주소 확인 중... {progress.done}/{progress.total}건
              </ProgressLabel>
            </Box>
          )}
        </>
      )}

      {(step === STEP.PREVIEW || step === STEP.SUBMITTING) && (
        <CorpExcelUploadPreviewTable
          excluded={excluded}
          rows={rows}
          onRowChange={handleRowFieldChange}
          disabled={step === STEP.SUBMITTING}
        />
      )}
    </CommonModalLayout>
  );
};

export default CorpExcelUploadModal;
